export default (params) => ({
    searchable: params.searchable,
    multiselect: params.multiselect,
    readonly: params.readonly,
    popover: false,
    search: '',
    emptyMessage: params.emptyMessage ?? 'No result found.',
    placeholder: params.placeholder,
    showPlaceholderWhenEmptyValue: params.showPlaceholderWhenEmptyValue,
    optionValue: params.optionValue,
    optionLabel: params.optionLabel,
    model: params.model,
    selectedOptions: [],
    countFiltered: -1,
    autoSelect: params.autoSelect,
    autoSelectOnClick: params.autoSelectOnClick,
    autoSelectedValue: null,
    autoSelectNotificationMessage: params.autoSelectNotificationMessage,
    wireModelPath: params.wireModelPath ?? null,
    wireModelLive: params.wireModelLive ?? false,
    init() {
        this.initMultiSelect()

        this.$watch('popover', status => {
            if (status) {
                this.filterOptions(this.search?.toLowerCase() ?? '')

                this.$nextTick(() => setTimeout(() => this.focusSelectItem(), 100))
            }
        })

        this.$watch('model', selected => {
            if (!this.multiselect && selected !== null && selected !== '' && selected !== undefined && !this.isValidOptionValue(selected)) {
                // Only clear invalid values while the user is interacting with the list.
                // During Livewire morphs the option nodes can be briefly absent.
                if (this.popover) {
                    this.model = null
                }

                return
            }

            this.syncSelected(selected)
        })

        this.$watch('search', search => this.filterOptions(search?.toLowerCase()))

        if (this.autoSelect) {
            if (this.model && this.optionsLength === 1) {
                this.autoSelectedValue = this.model
            }

            setInterval(() => this.selectFirstItem(), 100);
        }
    },
    togglePopover() {
        if (this.isBlocked()) return

        if (this.popover) {
            this.closePopover()
        } else {
            this.openPopover()
        }
    },
    openPopover() {
        this.popover = true

        if(this.$refs.select != null) {
            this.$refs.select.dispatchEvent(new Event('open'))
        }

        if (this.autoSelectOnClick) {
            this.selectFirstItem()
        }
    },
    closePopover() {
        this.popover = false

        this.revertPendingSearchState()

        if(this.$refs.select != null) {
            this.$refs.select.dispatchEvent(new Event('close'))
        }
    },
    revertPendingSearchState() {
        this.resetInvalidModel()

        this.search = ''
        this.filterOptions('')
    },
    resetInvalidModel() {
        if (this.multiselect || !this.popover) {
            return
        }

        if (!this.model?.toString().length) {
            return
        }

        if (this.isValidOptionValue(this.model)) {
            return
        }

        this.model = null
    },
    setModel(value) {
        if (!this.multiselect && !this.isValidOptionValue(value)) {
            return
        }

        this.model = value
    },
    isValidOptionValue(value) {
        if (this.multiselect) {
            return true
        }

        if (value === null || value === undefined || value === '') {
            return true
        }

        return this.getOptionElement(value) !== null
    },
    isSelected(value) {
        if (this.multiselect) {
            return !!Object.values(this.model ?? []).find(option => option == value)
        }

        return value == this.model
    },
    isBlocked() {
        return this.readonly || (typeof this.$refs.select !== 'undefined' && this.$refs.select.disabled)
    },
    selectChanged() {
        this.$refs.select?.dispatchEvent(new Event('select'))
        this.$refs.select?.dispatchEvent(new Event('change'))
    },
    unSelect(value) {
        if (this.isBlocked()) return

        let index = this.selectedOptions.findIndex(option => option.value == value)

        this.selectedOptions.splice(index, 1)

        index = this.model.findIndex(selected => selected == value)

        this.model.splice(index, 1)
    },
    select(value) {
        if (this.autoSelectedValue && this.autoSelectedValue == value && this.optionsLength === 1) {
            this.closePopover()

            return
        }

        if (this.isBlocked()) return

        if (this.multiselect && !value) {
            this.selectAll()

            return
        }

        this.search = ''

        if (this.multiselect) {
            this.model  = Object.assign([], this.model)
            const index = this.model.findIndex(selected => selected == value)

            if (~index) {
                return this.unSelect(value)
            }

            const { dataset: option } = this.getOptionElement(value)

            this.selectedOptions.push(option)

            return this.model.push(value)
        }

        if (value == this.model) {
            value = null
        }

        if (!this.isValidOptionValue(value)) {
            return
        }

        this.setModel(value)

        this.$refs.select.focus()

        this.closePopover()
    },
    selectAll(){
        const allOptions = this.$root.querySelector('[data-container=optionsContainer]').querySelectorAll(`[data-value]`)

        allOptions.forEach(option => {
            if (option.getAttribute('data-label') != 'Select All' && !this.isSelected(option.getAttribute('data-value'))) {
                this.select(option.getAttribute('data-value'))
            }
        })

        this.$refs.select.focus()

        this.closePopover()
    },
    clearModel() {
        const value          = this.multiselect ? []: null
        this.setModel(value)
        this.selectedOptions = []

        this.$refs.select.dispatchEvent(new Event('clear'))
        this.$refs.select.dispatchEvent(new Event('change'))
    },
    isEmptyModel() {
        if (this.multiselect) {
            return this.model?.length == 0
        }

        return this.model == null
    },
    getOptionElement(value) {
        return this.$root.querySelector('[data-container=optionsContainer]').querySelector(`[data-value='${value}']`)
    },
    getPlaceholderText() {
        if (this.showPlaceholderWhenEmptyValue && !this.getValueText()) {
            return this.placeholder
        }

        if (this.model?.toString().length) return null

        return this.placeholder
    },
    getValueText() {
        if (this.multiselect || !this.model?.toString().length) return null

        const option = this.getOptionElement(this.model)

        if (option === null) {
            return null
        }

        return this.decodeSpecialChars(option?.dataset?.label || null)
    },
    isAvailableInList(search, option) {
        const label = this.decodeSpecialChars(option.dataset.label)
        const value = this.decodeSpecialChars(option.dataset.value)

        return label.toLowerCase().includes(search)
            || value.toLowerCase().includes(search)
    },
    filterOptions(search) {
        const options = [...this.$root.querySelector('[data-container=optionsContainer]').children]
        this.countFiltered = 0
        const term = (search ?? '').toLowerCase()

        options.map(option => {
            if (this.isAvailableInList(term, option)) {
                option.classList.remove('hidden')
                this.countFiltered += 1
            } else {
                option.classList.add('hidden')
            }
        })
    },
    initMultiSelect() {
        if (!this.multiselect) return

        if (typeof this.model === 'string') {
            this.model = []
        }

        this.model?.map(selected => {
            const { dataset: option } = this.getOptionElement(selected)

            this.selectedOptions.push(option)
        })
    },
    modelWasChanged() {
        return this.model?.toString()
            !== this.selectedOptions.map(option => option.value).toString()
    },
    syncSelected(newModel) {
        if (!this.modelWasChanged()) return

        if(this.multiselect) {
            this.selectedOptions = this.model?.map(option => {
                return this.getOptionElement(option).dataset
            }) ?? []
        }

        this.selectChanged()
    },
    decodeSpecialChars(text) {
        const textarea     = document.createElement('textarea')
        textarea.innerHTML = text

        return textarea.value
    },
    focusSelectItem() {
        const selected = this.$refs.selectContainer.querySelector('ul li.selected');

        if (selected && selected.checkVisibility()) {
            selected.focus()
        } else if (this.$refs.search) {
            this.$refs.search.focus()
        } else {
            this.$refs.selectContainer.querySelector('ul li')?.focus()
        }
    },
    selectFirstItem() {
        if (!this.$el.checkVisibility() || this.model || this.optionsLength !== 1) return

        const value = this.$refs.selectContainer.querySelector('ul li')?.getAttribute('x-on:click').split("('")[1]?.split("')")[0]

        if (!this.isValidOptionValue(value)) return

        this.setModel(value)

        this.autoSelectedValue = value

        this.closePopover()

        if (this.autoSelectNotificationMessage) {
            const now = Date.now()

            if (localStorage.getItem('autoSelectNotificationMessageTimeout') > now) {
                return
            }

            this.$dispatch('notify', {
                type: 'success',
                title: this.autoSelectNotificationMessage
            });

            localStorage.setItem('autoSelectNotificationMessageTimeout', now + 1000)
        }
    },
    get optionsLength() {
        return this.$root.querySelector('[data-container=optionsContainer]').querySelectorAll('li:not([data-label="Empty Options"])').length
    }
})
